import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AuthMedia from "../components/AuthMedia";
import { Button } from "../components/ui/button";
import { BookOpen, LayoutGrid, ArrowLeft, Music2, Image as ImageIcon, PlusCircle, ChevronLeft, ChevronRight, Infinity as InfinityIcon, Video, CalendarClock } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};
const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};

const Libro = () => {
  const [memories, setMemories] = useState(null);
  const [cover, setCover] = useState(null);
  const [mode, setMode] = useState("book"); // book | archive
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState("next"); // next | prev — direzione dello sfoglio
  const navigate = useNavigate();
  const { user } = useAuth();

  const goPage = (target, direction) => {
    setDir(direction);
    setPage(target);
  };

  useEffect(() => {
    api.get("/memories?scope=capsule").then((res) => {
      const sorted = [...res.data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMemories(sorted);
    }).catch(() => setMemories([]));
    api.get("/profile").then((res) => setCover(res.data.cover_image)).catch(() => {});
  }, []);

  const firstImage = (m) => (m.media || []).find((x) => x.kind === "image");

  const modeBtn = (val, Icon, label, tid) => (
    <button
      onClick={() => setMode(val)}
      data-testid={tid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
        mode === val ? "bg-primary text-white" : "bg-[#F0E9DC] text-[#5a5148] hover:bg-[#e7ddca]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  const hasCover = !!cover;
  const totalPages = (memories ? memories.length : 0) + (hasCover ? 1 : 0);
  const isCoverPage = hasCover && page === 0;
  const current = memories && memories[hasCover ? page - 1 : page];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-in">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <button onClick={() => navigate("/ricordi")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="libro-back">
            <ArrowLeft className="w-4 h-4" /> Torna ai ricordi
          </button>
          <div className="flex items-center gap-2" data-testid="libro-mode-toggle">
            {modeBtn("book", BookOpen, "Libro", "libro-mode-book")}
            {modeBtn("archive", LayoutGrid, "Archivio", "libro-mode-archive")}
          </div>
        </div>

        {memories === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : memories.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-border" data-testid="libro-empty">
            <div className="w-16 h-16 rounded-2xl bg-[#FBEDE7] flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-serif text-2xl mb-2">Il tuo libro è ancora vuoto</h3>
            <p className="text-muted-foreground max-w-sm mx-auto mb-6">Aggiungi il primo ricordo per iniziare a comporre le pagine.</p>
            <Button onClick={() => navigate("/ricordi/nuovo")} className="rounded-full bg-primary hover:bg-[#c9694f] text-white h-11 px-8" data-testid="libro-create-btn">
              <PlusCircle className="w-4 h-4 mr-2" /> Crea il primo ricordo
            </Button>
          </div>
        ) : mode === "book" ? (
          <div data-testid="libro-book">
            <div className="book-perspective">
            <div key={page} className={dir === "prev" ? "page-turn-prev" : "page-turn-next"}>
            {isCoverPage ? (
              <article className="rounded-3xl border border-[#E7DECB] shadow-sm overflow-hidden relative min-h-[60vh] flex" data-testid="libro-cover-page">
                <AuthMedia item={cover} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
                <div className="relative mt-auto p-8 sm:p-12 text-white">
                  <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center mb-4">
                    <InfinityIcon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm uppercase tracking-[0.25em] text-white/80 mb-2">Il libro dei ricordi di</p>
                  <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{user?.name}</h2>
                  <p className="mt-3 text-white/85 max-w-md">Sfoglia le pagine: ogni ricordo è un pezzo di una vita che continua.</p>
                </div>
              </article>
            ) : (
            <article className="bg-[#FCFAF5] rounded-3xl border border-[#E7DECB] shadow-sm p-8 sm:p-12 min-h-[60vh] relative overflow-hidden" data-testid={`libro-page-${current.id}`}>
              <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-gradient-to-b from-primary/30 via-[#E7DECB] to-[#81B29A]/30" aria-hidden />
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{fmtDate(current.created_at)}</p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-6 leading-tight">{current.title}</h2>
              {current.content && (
                <p className="whitespace-pre-wrap leading-relaxed text-[#3d3a38] text-lg first-letter:text-5xl first-letter:font-serif first-letter:text-primary first-letter:mr-2 first-letter:float-left first-letter:leading-none">
                  {current.content}
                </p>
              )}
              {current.song && (
                <div className="flex items-center gap-2 bg-[#F0EAF4] rounded-xl p-3 mt-6" data-testid="libro-song">
                  <Music2 className="w-4 h-4 text-[#8A6D9C] shrink-0" />
                  <div className="flex-1"><AuthMedia item={current.song} /></div>
                </div>
              )}
              {current.media?.some((x) => x.kind === "image") && (
                <div className="mt-6" data-testid="libro-photos">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-[#81B29A]" /> Foto</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {current.media.filter((x) => x.kind === "image").map((item) => (
                      <div key={item.storage_path} className="rounded-xl overflow-hidden border border-[#E7DECB]">
                        <AuthMedia item={item} className="w-full h-56 object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {current.media?.some((x) => x.kind === "video") && (
                <div className="mt-6" data-testid="libro-videos">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-primary" /> Videomessaggi</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {current.media.filter((x) => x.kind === "video").map((item) => (
                      <div key={item.storage_path} className="rounded-xl overflow-hidden border border-[#E7DECB] relative">
                        <AuthMedia item={item} className="w-full h-56 object-cover" />
                        {(item.uploaded_at || current.created_at) && (
                          <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[11px] px-2 py-1 flex items-center gap-1">
                            <CalendarClock className="w-3 h-3" /> Caricato il {fmtDay(item.uploaded_at || current.created_at)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {current.media?.some((x) => x.kind !== "image" && x.kind !== "video") && (
                <div className="grid sm:grid-cols-2 gap-3 mt-6">
                  {current.media.filter((x) => x.kind !== "image" && x.kind !== "video").map((item) => (
                    <div key={item.storage_path} className="rounded-xl overflow-hidden border border-[#E7DECB]">
                      <div className="p-3 bg-[#F4F1EA]"><AuthMedia item={item} /></div>
                    </div>
                  ))}
                </div>
              )}
              <p className="absolute bottom-5 right-8 text-sm text-muted-foreground font-serif">{page + 1} / {totalPages}</p>
            </article>
            )}
            </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <Button variant="outline" disabled={page === 0} onClick={() => goPage(Math.max(0, page - 1), "prev")} className="rounded-full border-border h-11 px-5" data-testid="libro-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Pagina precedente
              </Button>
              <Button variant="outline" disabled={page >= totalPages - 1} onClick={() => goPage(Math.min(totalPages - 1, page + 1), "next")} className="rounded-full border-border h-11 px-5" data-testid="libro-next">
                Pagina successiva <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="libro-archive">
            {memories.map((m) => {
              const img = firstImage(m);
              return (
                <button
                  key={m.id}
                  onClick={() => navigate(`/ricordi/${m.id}`)}
                  className="group text-left rounded-2xl overflow-hidden border border-border bg-white shadow-sm hover:-translate-y-1 hover:shadow-md transition-transform aspect-square flex flex-col"
                  data-testid={`libro-tile-${m.id}`}
                >
                  <div className="flex-1 min-h-0 bg-[#F4F1EA] flex items-center justify-center overflow-hidden">
                    {img ? (
                      <AuthMedia item={img} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-[#c9a892]" />
                    )}
                  </div>
                  <div className="p-3 shrink-0">
                    <p className="text-[11px] text-muted-foreground">{fmtDate(m.created_at)}</p>
                    <h3 className="font-serif text-sm leading-snug line-clamp-2">{m.title}</h3>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Libro;
